import { memo } from 'react';
import { Avatar, Tooltip, TooltipProps } from '@mui/material';
import { SxProps } from '@mui/system';
import gravatarUrl from 'gravatar-url';
import { GetUserMeData } from '../../utility/types/dataProvider';
import { getTextColor } from 'utility/styles';
import { stringToColor } from 'utility/styles';
import { useTheme } from '@mui/material/styles';
import { PopperPropsExtended } from 'components/IconLabelFields/IconLabelTextField';
import { formatAuthorData } from 'utility/functions';

type UserAvatarProps = {
  user?: GetUserMeData | undefined;
  size?: number;
  placement?: TooltipProps['placement'];
  sx?: SxProps;
  customTitle?: React.ReactNode;
};

export const UserAvatar = memo(
  ({ user, size = 64, placement = undefined, sx, customTitle }: UserAvatarProps) => {
    const theme = useTheme();
    if (!user) {
      return null;
    }

    let avatar = user?.avatar;
    let email = user?.email || 'example@example.com'; // Default email address if user is undefined
    let name = formatAuthorData(user, { includeEmail: true, fallback: '' });

    if (!avatar) {
      // Use gravatar if no avatar is available
      avatar = gravatarUrl(email, {
        size: size,
        default: 'mm',
      });
    }

    const avatarColor = stringToColor(email);
    const textColor = getTextColor(avatarColor, theme);

    const avatarComponent = (
      <Avatar
        src={avatar}
        alt={name || email}
        children={name ? name[0] : email[0]}
        data-private={true}
        sx={{
          bgcolor: avatarColor, // Make sure avatarColor is defined
          color: textColor,
          textTransform: 'capitalize',
          '&.MuiAvatar-root': {
            width: size,
            height: size,
          },
          ...sx, // Spread any additional styles passed via props
        }}
      />
    );

    if (name && placement) {
      return (
        <Tooltip
          title={customTitle || name}
          placement={placement}
          slotProps={{
            popper: {
              'data-private': true,
            } as PopperPropsExtended,
          }}
        >
          {avatarComponent}
        </Tooltip>
      );
    }

    return avatarComponent;
  },
);

export default UserAvatar;
